/**
 * Intentional request cancellation.
 *
 * Kept free of imports so stores, views and the Sentry plugin can use it without
 * pulling in `errorHandler.js`, which reaches the whole store index.
 */

/**
 * What a request the CRM cancelled on purpose rejects with: a route change
 * aborting its in-flight requests, or a superseded fetch.
 *
 * An `Error`, unlike every other result of `processError`, so the Sentry
 * `beforeSend` filter can recognise it by `hint.originalException` wherever it
 * surfaces — Vue's error handler (MOVEPRO-CRM-DY), `onunhandledrejection`
 * (MOVEPRO-CRM-K) or `onerror` — without having to filter on the plain-object
 * shape every real API failure shares.
 *
 * It carries the same fields as the other results, so callers read it the same
 * way. `errors` is `{}`, not `null`: vee-validate's `setErrors(null)` throws
 * (`Object.keys(null)`), and several forms pass a caught error's `errors`
 * straight to it, while `setErrors({})` is a no-op.
 *
 * `name` and `code` match axios's own `CanceledError`, so the guards already
 * spread through the CRM (`error.name === 'CanceledError'`,
 * `error.code === 'ERR_CANCELED'`, `message.includes('canceled')`) keep working.
 */
export class RequestCanceledError extends Error {
    constructor(message = 'canceled') {
        super(message);
        this.name = 'CanceledError';
        this.code = 'ERR_CANCELED';
        this.errors = {};
        this.links = null;
        this.data = null;
        this.isCanceledRequest = true;
    }
}

/**
 * Whether an error is an intentional cancellation: ours from `processError`, or
 * axios's raw `CanceledError` from a request that never went through the
 * interceptor.
 */
export function isCanceledRequest(error) {
    return (
        error instanceof RequestCanceledError ||
        error?.isCanceledRequest === true ||
        error?.name === 'CanceledError' ||
        error?.code === 'ERR_CANCELED'
    );
}
